import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { api } from '../api/client'
import type { Schrank } from '../api/typen'
import AppRahmen from '../komponenten/AppRahmen'
import Kamera from '../komponenten/Kamera'

/**
 * P2-02: Gesamtfoto des Schranks. Es dient der Zuordnung und Nachvollziehbarkeit, nicht der
 * Analyse, und lässt sich deshalb jederzeit nachholen oder ersetzen — nicht nur beim Anlegen.
 */
export default function SchrankGesamtfoto() {
  const { schrankId } = useParams()
  const navigiere = useNavigate()
  const abfragen = useQueryClient()

  const schrank = useQuery({
    queryKey: ['schrank', schrankId],
    queryFn: () => api.get<Schrank>(`/api/schraenke/${schrankId}`),
  })

  const hochladen = useMutation({
    mutationFn: (bild: Blob) =>
      api.bild<Schrank>(`/api/schraenke/${schrankId}/gesamtfoto`, bild, 'gesamt.jpg'),
    onSuccess: () => {
      abfragen.invalidateQueries({ queryKey: ['schrank', schrankId] })
      abfragen.invalidateQueries({ queryKey: ['versorgungspunkt'] })
      navigiere(`/schraenke/${schrankId}`)
    },
  })

  return (
    <AppRahmen
      titel={schrank.data?.bezeichnung ?? 'Schrank'}
      unter={schrank.data?.gesamtfotoVorhanden ? 'Gesamtfoto ersetzen' : 'Gesamtfoto aufnehmen'}
      zurueck={`/schraenke/${schrankId}`}
      pfad={schrank.data?.pfad}
      ebene="schrank"
      pfadZusatz="Gesamtfoto"
    >
      <p className="sek">
        Den ganzen Schrank ins Bild nehmen. Das Foto dient der Zuordnung und Nachvollziehbarkeit, es wird
        nicht ausgewertet.
      </p>

      {schrank.data?.gesamtfotoVorhanden && (
        <div className="gruppe">
          <span className="label">Bisheriges Foto</span>
          <img
            src={`/api/schraenke/${schrankId}/gesamtfoto`}
            alt="Bisheriges Gesamtfoto"
            style={{ borderRadius: 8, border: '1px solid var(--linie)' }}
          />
        </div>
      )}

      <Kamera
        gesperrt={hochladen.isPending}
        hinweis={hochladen.isPending ? 'Wird gespeichert …' : undefined}
        beiAufnahme={(bild) => hochladen.mutate(bild)}
      />

      {hochladen.isError && <p className="hinweiszeile">{(hochladen.error as Error).message}</p>}
    </AppRahmen>
  )
}
